import { useEscapeKey } from '@/hooks/useEscapeKey';
import { useMemo, useState } from 'react';

export type LinkOption = { id: string; label: string; image?: string; code?: string };

/** Předpona hodnoty, která říká „tohle je nová položka, založ ji při uložení". */
export const NEW_PREFIX = 'new:';

/**
 * Vnořené modal okno pro výběr navázaného záznamu (schema_link). Hledání + seznam
 * s náhledovými obrázky (když je má cílová tabulka). „Zrušit propojení" → prázdno.
 */
export function LinkPickerModal({
  title,
  options,
  currentId,
  onPick,
  onClose,
  allowNew,
}: {
  title: string;
  options: LinkOption[];
  currentId: string;
  onPick: (id: string) => void;
  onClose: () => void;
  /** Dovolit založení nové položky rovnou z výběru (jen odvozené nabídky). */
  allowNew?: boolean;
}) {
  useEscapeKey(onClose);
  const [q, setQ] = useState('');
  const [kod, setKod] = useState('');

  // Náhledy jen když je aspoň jeden má (nitě je nemají → žádná prázdná kolečka).
  const hasImages = useMemo(() => options.some((o) => o.image), [options]);
  // Pole „Kód" jen když položky kódy mají (produkty).
  const hasCodes = useMemo(() => options.some((o) => o.code), [options]);

  // Kód filtruje podle začátku („T" → T1…, „BU" → bundy), hledání kdekoli v názvu i kódu.
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const k = kod.trim().toLowerCase();
    if (!s && !k) return options;
    return options.filter(
      (o) =>
        (!k || (o.code ?? '').toLowerCase().startsWith(k)) &&
        (!s || o.label.toLowerCase().includes(s) || (o.code ?? '').toLowerCase().includes(s)),
    );
  }, [q, kod, options]);

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 overflow-y-auto">
      <button
        type="button"
        aria-label="Zavřít"
        onClick={onClose}
        className="fixed inset-0 bg-black/40 cursor-default"
      />
      <div className="relative w-full max-w-md my-10 bg-surface rounded-2xl shadow-modal reveal flex flex-col max-h-[80vh]">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h3 className="text-base font-bold text-text truncate pr-4">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Zavřít"
            className="text-text-muted hover:text-text text-xl leading-none cursor-pointer shrink-0"
          >
            ✕
          </button>
        </div>

        <div className="px-5 py-3 border-b border-border-light flex gap-2">
          <input
            // biome-ignore lint/a11y/noAutofocus: okno se otevírá kliknutím a hledání je jeho jediný účel
            autoFocus
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Hledat…"
            className="min-w-0 flex-1 border border-border rounded-lg px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />
          {hasCodes && (
            <input
              type="search"
              value={kod}
              onChange={(e) => setKod(e.target.value)}
              placeholder="Kód…"
              aria-label="Začátek kódu produktu"
              className="w-28 shrink-0 border border-border rounded-lg px-3 py-2 text-sm font-mono text-text focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
          )}
        </div>

        <div className="overflow-y-auto p-2">
          <button
            type="button"
            onClick={() => onPick('')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left text-sm cursor-pointer hover:bg-surface-hover ${
              currentId === '' ? 'bg-primary-50 font-semibold' : ''
            }`}
          >
            <span className="text-text-muted italic">— nevybráno —</span>
          </button>
          {filtered.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => onPick(o.id)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left text-sm cursor-pointer hover:bg-surface-hover ${
                o.id === currentId ? 'bg-primary-50 font-semibold' : ''
              }`}
            >
              {hasImages &&
                (o.image ? (
                  <img
                    src={o.image}
                    alt=""
                    loading="lazy"
                    // object-contain: fotky produktů se nesmí ořezávat, ať je vidět celý kus.
                    className="h-10 w-10 shrink-0 object-contain rounded-lg border border-border bg-white"
                  />
                ) : (
                  <span className="h-10 w-10 shrink-0 rounded-lg bg-border-light" />
                ))}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-text">{o.label}</span>
                {o.code && <span className="block text-xs text-text-muted">{o.code}</span>}
              </span>
            </button>
          ))}
          {filtered.length === 0 && !allowNew && (
            <p className="px-3 py-4 text-sm text-text-muted text-center">Nic nenalezeno.</p>
          )}
          {allowNew && q.trim() && !filtered.some((o) => o.label.trim() === q.trim()) && (
            <button
              type="button"
              onClick={() => onPick(`${NEW_PREFIX}${q.trim()}`)}
              className="w-full flex items-center gap-3 px-3 py-2 mt-1 rounded-lg text-left text-sm cursor-pointer border border-dashed border-action/50 bg-action-light text-action-dark hover:opacity-90"
            >
              + Přidat „{q.trim()}" jako novou položku
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
